import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./index.css";

const BASE = process.env.REACT_APP_URL || "http://127.0.0.1:8000";
const API_KEY = process.env.REACT_APP_API_KEY;
const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});

// ─── Design tokens ──────────────────────────────────────────────────────────
const C = {
  ink:    "#1f2733",   // primary text
  body:   "#374151",   // body text
  muted:  "#6b7280",   // secondary text
  faint:  "#9ca3af",   // tertiary / axis
  line:   "#e5e7eb",   // borders
  grid:   "#eef0f3",   // chart gridlines
  slate:  "#64748b",   // sleek neutral bar
  navy:   "#0f2a52",
  orange: "#f98300",
  green:  "#0a8f5f",
  red:    "#d23434",
};

// ─── API ──────────────────────────────────────────────────────────────────────
async function fetchSymbolDetail(symbol) {
  const res = await fetch(`${BASE}/api/symbols/${symbol}/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error(`Symbol not found: ${symbol}`);
  return res.json();
}

async function fetchChartData(symbol, fromDate, toDate) {
  const res = await fetch(
    `${BASE}/api/chart/${symbol}?from_date=${fromDate}&to_date=${toDate}&is_index=`,
    { headers: apiHeaders() }
  );
  if (!res.ok) throw new Error("Chart data unavailable");
  const data = await res.json();
  return (data.candles || []).map(c => ({
    date: c.timestamp,
    close: parseFloat(c.close_price),
    open: parseFloat(c.open_price),
    high: parseFloat(c.high_price),
    low: parseFloat(c.low_price),
    volume: c.volume,
    ldcp: parseFloat(c.ldcp),
  }));
}

async function fetchIntradayData(symbol) {
  const res = await fetch(`${BASE}/api/intraday/${symbol}`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Intraday data unavailable");
  const json = await res.json();
  return (json.data || [])
    .map(([ts, price, volume]) => ({
      date: new Date(ts * 1000).toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" }),
      close: price,
      volume,
      ldcp: null,
    }))
    .reverse();
}

async function fetchAnnouncements(symbol) {
  const res = await fetch(`${BASE}/api/company/announcements?symbol=${symbol}`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Announcements unavailable");
  return res.json();
}

async function fetchFinancials(symbol) {
  const res = await fetch(`${BASE}/api/company/${symbol}/financials/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Financials unavailable");
  return res.json();
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(d) { return d.toISOString().split("T")[0]; }
function getDateRange(days) {
  const to = new Date(), from = new Date();
  from.setDate(from.getDate() - days);
  return { from: formatDate(from), to: formatDate(to) };
}
function fmtNum(v, dec = 2) {
  if (v === null || v === undefined) return "—";
  return parseFloat(v).toFixed(dec);
}
function fmtLarge(v) {
  if (!v) return "—";
  return (v / 1e6).toFixed(2) + " mn";
}

// ─── Injected CSS for responsive layout ──────────────────────────────────────
const PAGE_CSS = `
.td-stat-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:0;margin:0 0 1.25rem;background:#fff;border:1px solid ${C.line};border-radius:10px;overflow:hidden;}
.td-stat-cell{padding:14px 18px;border-right:1px solid ${C.line};border-bottom:1px solid ${C.line};}
.td-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:1.25rem;}
.td-grid-4{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;}
.td-range-btns{display:flex;gap:6px;}
.td-company-left{display:flex;align-items:center;gap:16px;}
.td-header-inner{display:flex;justify-content:space-between;align-items:center;}
@media(max-width:760px){
  .td-grid-2{grid-template-columns:1fr!important;}
  .td-grid-4{grid-template-columns:1fr 1fr!important;}
  .td-range-btns{flex-wrap:wrap!important;}
  .td-header-inner{flex-direction:column!important;align-items:flex-start!important;gap:14px!important;}
  .td-company-left{align-items:center!important;}
}
`;
function injectStyles() {
  const existing = document.getElementById("td-styles");
  if (existing) existing.remove();
  const t = document.createElement("style");
  t.id = "td-styles";
  t.textContent = PAGE_CSS;
  document.head.appendChild(t);
}

// ─── Range buttons ────────────────────────────────────────────────────────────
const RANGES = [
  { label: "1D", days: 0 },
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
const RangeBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: "5px 12px", borderRadius: 6, border: "1px solid",
    borderColor: active ? C.orange : C.line,
    background: active ? C.orange : "#fff",
    color: active ? "#fff" : C.muted,
    fontWeight: 600, fontSize: 12, cursor: "pointer",
    fontFamily: "inherit", whiteSpace: "nowrap", transition: "all .15s",
  }}>{label}</button>
);

const InfoRow = ({ label, value }) => (
  <div className="data-row">
    <span className="data-label">{label}</span>
    <span className="data-value" style={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>
      {value ?? "—"}
    </span>
  </div>
);

// Clean stat cell used inside the top stat strip
const StatCell = ({ label, value, sub, color }) => (
  <div className="td-stat-cell">
    <div style={{ fontSize: 11, color: C.muted, marginBottom: 5, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    <div style={{ fontSize: "1rem", fontWeight: 700, color: color || C.ink }}>{value ?? "—"}</div>
    {sub && <div style={{ fontSize: 11, color: color || C.muted, marginTop: 3, fontWeight: 500 }}>{sub}</div>}
  </div>
);

const RangeBar = ({ label, low, high, current }) => {
  const noData = low == null || high == null;
  const pct = (!noData && current != null)
    ? Math.max(0, Math.min(100, ((current - low) / (high - low)) * 100))
    : null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: C.body, fontWeight: 600 }}>{label}</span>
        {noData
          ? <span style={{ fontSize: 11, color: C.faint }}>No data</span>
          : <div style={{ display: "flex", gap: 10, fontSize: 12 }}>
              <span style={{ color: C.red, fontWeight: 600 }}>L {fmtNum(low)}</span>
              <span style={{ color: C.line }}>|</span>
              <span style={{ color: C.green, fontWeight: 600 }}>H {fmtNum(high)}</span>
            </div>
        }
      </div>
      <div style={{ height: 5, background: "#eef0f3", borderRadius: 6, position: "relative" }}>
        {!noData && (
          <div style={{
            position: "absolute", inset: 0, height: 5, borderRadius: 6,
            background: "linear-gradient(90deg,#e2675f 0%,#f0b15a 50%,#5bb98c 100%)",
          }} />
        )}
        {pct !== null && (
          <div style={{
            width: 12, height: 12, background: C.navy, borderRadius: "50%",
            position: "absolute", top: -3.5, border: "2px solid #fff",
            boxShadow: "0 1px 3px rgba(0,0,0,.22)",
            left: `calc(${pct.toFixed(1)}% - 6px)`,
          }} />
        )}
      </div>
    </div>
  );
};

const PerfTile = ({ label, value }) => {
  const isPos = value >= 0;
  return (
    <div style={{ background: "#f8f9fb", border: `1px solid ${C.line}`, borderRadius: 8, padding: "14px 10px", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, fontWeight: 500 }}>{label}</div>
      {value === null || value === undefined
        ? <div style={{ fontSize: 14, color: C.faint }}>—</div>
        : <>
            <div style={{ fontSize: 16, fontWeight: 700, color: isPos ? C.green : C.red }}>
              {isPos ? "+" : ""}{fmtNum(value)}%
            </div>
            <div style={{ marginTop: 8, height: 3, background: "#e5e7eb", borderRadius: 3 }}>
              <div style={{
                height: 3, borderRadius: 3,
                background: isPos ? C.green : C.red,
                width: `${Math.min(100, Math.abs(value) * 3)}%`,
              }} />
            </div>
          </>
      }
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: `1px solid ${C.line}`,
      borderRadius: 8, padding: "8px 12px", fontSize: 12,
      boxShadow: "0 4px 14px rgba(16,24,40,0.10)",
    }}>
      <div style={{ fontWeight: 700, color: C.ink, marginBottom: 4 }}>{label}</div>
      {payload.map(p => p.value != null && (
        <div key={p.name} style={{ color: p.color, marginBottom: 1 }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// shared axis styling
const axisTick = { fontSize: 11, fill: C.faint };

// ─── Main ─────────────────────────────────────────────────────────────────────
const PSXDashboard = () => {
  const { symbol } = useParams();
  const history = useHistory();

  const [symbolData, setSymbolData] = useState(null);
  const [chartData, setChartData]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError]           = useState(null);
  const [activeRange, setActiveRange] = useState(RANGES[0]); // 1D
  const [announcements, setAnnouncements] = useState([]);
  const [financials, setFinancials] = useState(null);

  // ── sticky header + section navigation ──────────────────────────────────
  const stickyRef     = useRef(null);
  const [headerOffset, setHeaderOffset] = useState(134); // height of fixed site header
  const [activeSection, setActiveSection] = useState("chart");
  const [collapsed, setCollapsed] = useState(false); // slim header once scrolled

  // section refs
  const secRefs = {
    chart:         useRef(null),
    performance:   useRef(null),
    about:         useRef(null),
    announcements: useRef(null),
    earnings:      useRef(null),
    eps:           useRef(null),
    dividends:     useRef(null),
    financials:    useRef(null),
    ohlcv:         useRef(null),
  };

  // measure the fixed site-header height (the .hdr-offset padding-top)
  useEffect(() => {
    const measure = () => {
      const off = document.querySelector(".hdr-offset");
      if (off) {
        const pt = parseInt(getComputedStyle(off).paddingTop, 10);
        if (!isNaN(pt)) setHeaderOffset(pt);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const scrollToSection = (id) => {
    const el = secRefs[id]?.current;
    if (!el) return;
    const stickyH = stickyRef.current?.offsetHeight || 0;
    const y = window.scrollY + el.getBoundingClientRect().top - headerOffset - stickyH - 10;
    window.scrollTo({ top: y, behavior: "smooth" });
    setActiveSection(id);
  };

  // scroll-spy: highlight the section currently under the sticky bar.
  // Throttled via rAF + only updates state when the value actually changes,
  // so we don't re-render the (chart-heavy) component on every scroll frame.
  useEffect(() => {
    let ticking = false;
    const compute = () => {
      ticking = false;
      const stickyH = stickyRef.current?.offsetHeight || 0;
      const threshold = headerOffset + stickyH + 24;
      let current = null;
      Object.entries(secRefs).forEach(([id, ref]) => {
        const el = ref.current;
        if (el && el.getBoundingClientRect().top <= threshold) current = id;
      });
      if (current) setActiveSection(prev => (prev === current ? prev : current));
      const wantCollapsed = window.scrollY > 60;
      setCollapsed(prev => (prev === wantCollapsed ? prev : wantCollapsed));
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(compute);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerOffset, financials, announcements]);

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    fetchSymbolDetail(symbol)
      .then(setSymbolData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    fetchAnnouncements(symbol)
      .then(d => setAnnouncements(d.results || []))
      .catch(() => setAnnouncements([]));
    fetchFinancials(symbol)
      .then(setFinancials)
      .catch(() => setFinancials(null));
  }, [symbol]);

  useEffect(() => {
    if (!symbol) return;
    setChartLoading(true);
    const loader = activeRange.days === 0
      ? fetchIntradayData(symbol)
      : (() => { const { from, to } = getDateRange(activeRange.days); return fetchChartData(symbol, from, to); })();
    loader
      .then(setChartData)
      .catch(() => setChartData([]))
      .finally(() => setChartLoading(false));
  }, [symbol, activeRange]);

  if (loading) return (
    <div className="psx1-dashboard" style={{ padding: "5rem 2rem", textAlign: "center" }}>
      <div style={{ color: C.orange }}>Loading {symbol}…</div>
    </div>
  );

  if (error || !symbolData) return (
    <div className="psx1-dashboard" style={{ padding: "5rem 2rem", textAlign: "center" }}>
      <div style={{ color: C.red, marginBottom: 16 }}>{error || "Symbol not found"}</div>
      <button onClick={() => history.push("/")} style={{
        background: C.orange, color: "#fff", border: "none",
        padding: "10px 24px", borderRadius: 8, cursor: "pointer", fontSize: 14,
      }}>← Go Home</button>
    </div>
  );

  const d       = symbolData;
  const details = d.details || {};
  const stats   = d.stats || {};
  const ranges  = stats.ranges || {};
  const perf    = stats.performance || {};

  const lastPrice = stats.last_price != null ? parseFloat(stats.last_price) : null;
  const changeVal = stats.change_val != null ? parseFloat(stats.change_val) : null;
  const changePercent = stats.change_percent != null ? parseFloat(stats.change_percent) : null;
  const isPos     = changePercent != null ? changePercent >= 0 : true;

  const closes   = chartData.map(c => c.close).filter(Boolean);
  const minClose = closes.length ? Math.min(...closes) * 0.995 : "auto";
  const maxClose = closes.length ? Math.max(...closes) * 1.005 : "auto";

  // section tabs (only those that have content)
  const TABS = [
    { id: "chart",         label: "Chart",         show: true },
    { id: "performance",   label: "Performance",   show: true },
    { id: "about",         label: "About",         show: true },
    { id: "announcements", label: "Announcements", show: announcements.length > 0 },
    { id: "earnings",      label: "Earnings",      show: !!financials?.earnings },
    { id: "eps",           label: "EPS",           show: !!financials?.earnings?.quarterly_chart?.length },
    { id: "dividends",     label: "Dividends",     show: !!financials?.dividends },
    { id: "financials",    label: "Financials",    show: !!financials?.profit?.history?.length },
    { id: "ohlcv",         label: "OHLCV",         show: true },
  ].filter(t => t.show);

  return (
    <div className="psx1-dashboard">

      {/* ── STICKY: HEADER CARD + TAB BAR ── */}
      <div className="td-sticky" ref={stickyRef} style={{ top: headerOffset }}>
        <div className="psx1-container">
          <div className="td-headcard" style={{ padding: collapsed ? "8px 16px" : "1rem 1.25rem", transition: "padding .2s" }}>
            <div className="psx1-header-content td-header-inner">

              <div className="td-company-left">
                {/* Logo */}
                <div style={{
                  width: collapsed ? 36 : 84, height: collapsed ? 36 : 84,
                  borderRadius: collapsed ? 8 : 10, background: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", flexShrink: 0, border: `1px solid ${C.line}`,
                  transition: "width .2s, height .2s",
                }}>
                  <img
                    src={`https://api.orbitsecurities.com.pk/images/symbols/${d.code}.svg`}
                    alt={d.code}
                    style={{ width: "85%", height: "85%", objectFit: "contain" }}
                    onError={e => {
                      e.target.style.display = "none";
                      e.target.parentNode.innerHTML =
                        `<span style="font-size:12px;font-weight:700;color:${C.orange};font-family:monospace">${d.code}</span>`;
                    }}
                  />
                </div>

                {/* Name + sector */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{
                      background: C.navy, color: "#fff",
                      padding: "2px 9px", borderRadius: 5,
                      fontSize: collapsed ? 13 : 15, fontFamily: "monospace", letterSpacing: "0.05em", fontWeight: 700,
                    }}>{d.code}</span>
                    <h1 style={{ margin: 0, fontSize: collapsed ? "1rem" : "1.25rem", fontWeight: 700, color: C.ink, transition: "font-size .2s" }}>
                      {d.name}
                    </h1>
                    {d.is_shariah && collapsed && (
                      <span style={{
                        background: "#e7f7f0", color: "#0a6b48",
                        fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                      }}>✓ Shariah</span>
                    )}
                  </div>
                  {!collapsed && (
                    <p style={{ margin: "5px 0 0", fontSize: 13, color: C.muted }}>
                      {d.sector?.name || "—"}
                      {d.is_shariah && (
                        <span style={{
                          marginLeft: 8, background: "#e7f7f0", color: "#0a6b48",
                          fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                        }}>✓ Shariah</span>
                      )}
                      {!d.is_active && (
                        <span style={{
                          marginLeft: 8, background: "#fdecec", color: "#a12727",
                          fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                        }}>Inactive</span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Price */}
              <div className="price-badge" style={collapsed ? { display: "flex", alignItems: "center", gap: 10, padding: "6px 12px" } : undefined}>
                <div className="price-value" style={collapsed ? { fontSize: "1.15rem" } : undefined}>
                  {lastPrice != null ? `Rs. ${fmtNum(lastPrice)}` : "N/A"}
                </div>
                {changePercent != null && (
                  <div style={{ marginTop: collapsed ? 0 : 3 }}>
                    <span className={`badge ${isPos ? "positive" : "negative"}`}>
                      {isPos ? "▲" : "▼"} {changeVal.toFixed(2)} ({changePercent.toFixed(2)}%)
                    </span>
                  </div>
                )}
                {stats.volume && !collapsed && (
                  <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>
                    Vol: {parseInt(stats.volume).toLocaleString()}
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Tab bar */}
          <div className="td-tabbar">
            <div className="td-tabs">
              {TABS.map(t => (
                <button
                  key={t.id}
                  className={`td-tab${activeSection === t.id ? " active" : ""}`}
                  onClick={() => scrollToSection(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="psx1-container" style={{ paddingTop: "1.25rem" }}>

        {/* ── STAT STRIP ── */}
        <div className="td-stat-row">
          <StatCell label="Latest Price"   value={lastPrice ? `Rs. ${fmtNum(lastPrice)}` : "—"} color={isPos ? C.green : C.red} sub={changePercent != null ? `${isPos ? "+" : ""}${fmtNum(changeVal)} (${isPos ? "+" : ""}${fmtNum(changePercent)}%)` : "—"} />
          <StatCell label="Previous Close"  value={stats.ldcp ? fmtNum(stats.ldcp).toLocaleString() : "—"} />
          <StatCell label="Today's Volume"  value={stats.volume ? parseInt(stats.volume).toLocaleString() : "—"} />
          <StatCell label="Avg Vol (12M)"   value={stats.avg_volume ? parseInt(stats.avg_volume).toLocaleString() : "—"} />
          <StatCell label="Total Shares"    value={fmtLarge(details.total_shares)} />
          <StatCell label="Free Float"      value={fmtLarge(details.free_float)} sub={details.free_float && details.total_shares ? `${((details.free_float / details.total_shares) * 100).toFixed(1)}% of total` : null} />
          <StatCell label="Face Value"      value={details.face_value ? `Rs. ${details.face_value}` : "—"} />
          <StatCell label="Year End"        value={details.year_end || "—"} />
        </div>

        {/* ── PRICE CHART (big) ── */}
        <div className="card summary-card" ref={secRefs.chart}>
          <div className="card-header" style={{ justifyContent: "space-between" }}>
            <h2 className="card-title">Price Chart — {d.code}</h2>
            <div className="td-range-btns">
              {RANGES.map(r => (
                <RangeBtn key={r.label} label={r.label} active={activeRange.label === r.label} onClick={() => setActiveRange(r)} />
              ))}
            </div>
          </div>
          <div className="card-content">
            {chartLoading ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: C.faint }}>Loading chart…</div>
            ) : chartData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: C.faint }}>No chart data for this range</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={380}>
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={C.orange} stopOpacity={0.16} />
                        <stop offset="95%" stopColor={C.orange} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                    <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={{ stroke: C.line }} interval="preserveStartEnd" minTickGap={40} />
                    <YAxis domain={[minClose, maxClose]} tick={axisTick} tickLine={false} axisLine={false} tickFormatter={v => v.toFixed(0)} width={46} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="close" name="Close"      stroke={C.orange} strokeWidth={2} fill="url(#priceGrad)" dot={false} activeDot={{ r: 4, fill: C.orange }} />
                    <Area type="monotone" dataKey="ldcp"  name="Prev Close" stroke="#cbd5e1" strokeWidth={1} strokeDasharray="4 4" fill="none" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 8 }}>
                  <ResponsiveContainer width="100%" height={70}>
                    <BarChart data={chartData} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                      <XAxis dataKey="date" hide />
                      <YAxis hide />
                      <Tooltip content={({ active, payload }) =>
                        active && payload?.length
                          ? <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 6, padding: "4px 8px", fontSize: 11 }}>
                              Vol: <strong>{payload[0]?.value?.toLocaleString()}</strong>
                            </div>
                          : null
                      } />
                      <Bar dataKey="volume" fill={C.slate} opacity={0.35} radius={[2, 2, 0, 0]} maxBarSize={6} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ textAlign: "center", fontSize: 10, color: C.faint }}>Volume</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── RANGES + PERFORMANCE ── */}
        <div className="td-grid-2" style={{ marginBottom: "1.25rem" }} ref={secRefs.performance}>
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h2 className="card-title">Price Ranges</h2></div>
            <div className="card-content">
              <RangeBar label="Day Range" low={ranges.day?.low}            high={ranges.day?.high}            current={lastPrice} />
              <RangeBar label="6 Months"  low={ranges.six_months?.low}     high={ranges.six_months?.high}     current={lastPrice} />
              <RangeBar label="52 Weeks"  low={ranges.fifty_two_week?.low}  high={ranges.fifty_two_week?.high}  current={lastPrice} />
            </div>
          </div>

          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h2 className="card-title">Performance</h2></div>
            <div className="card-content">
              <div className="td-grid-4">
                <PerfTile label="1 Month"  value={perf["1_month"]} />
                <PerfTile label="3 Months" value={perf["3_months"]} />
                <PerfTile label="6 Months" value={perf["6_months"]} />
                <PerfTile label="1 Year"   value={perf["1_year"]} />
              </div>
            </div>
          </div>
        </div>

        {/* ── COMPANY INFO + ABOUT ── */}
        <div className="td-grid-2" style={{ marginBottom: "1.25rem" }} ref={secRefs.about}>
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h2 className="card-title">Company Details</h2></div>
            <div className="card-content">
              <div className="data-rows">
                <InfoRow label="Sector"      value={d.sector?.name} />
                <InfoRow label="Sector Code" value={d.sector?.code} />
                <InfoRow label="Face Value"  value={details.face_value ? `Rs. ${details.face_value}` : null} />
                <InfoRow label="Year End"    value={details.year_end} />
                <InfoRow label="Auditor"     value={details.auditor} />
                <InfoRow label="Registrar"   value={details.registrar} />
                <InfoRow label="Address"     value={details.address} />
                <InfoRow label="Website"     value={
                  details.website
                    ? <a href={details.website} target="_blank" rel="noopener noreferrer"
                         style={{ color: C.orange, textDecoration: "none", wordBreak: "break-all" }}>
                        {details.website}
                      </a>
                    : null
                } />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h2 className="card-title">About</h2></div>
            <div className="card-content">
              <p style={{ fontSize: 13, color: C.body, lineHeight: 1.8, margin: 0 }}>
                {details.description || "No description available."}
              </p>
              {(details.total_shares || details.free_float) && (
                <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ background: "#f8f9fb", border: `1px solid ${C.line}`, borderRadius: 8, padding: "14px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Shares</div>
                    <div style={{ fontWeight: 700, color: C.ink }}>{fmtLarge(details.total_shares)}</div>
                  </div>
                  <div style={{ background: "#f8f9fb", border: `1px solid ${C.line}`, borderRadius: 8, padding: "14px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" }}>Free Float</div>
                    <div style={{ fontWeight: 700, color: C.green }}>{fmtLarge(details.free_float)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── ANNOUNCEMENTS ── */}
        {announcements.length > 0 && (
          <div className="card summary-card" ref={secRefs.announcements}>
            <div className="card-header"><h2 className="card-title">Recent Announcements</h2></div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 520 }}>
                <thead>
                  <tr style={{ background: "#f8f9fb", borderBottom: `2px solid ${C.line}` }}>
                    {["Ann Type", "Ann Date", "Description", "PDF"].map(h => (
                      <th key={h} style={{
                        padding: "11px 16px",
                        textAlign: h === "Description" ? "left" : "center",
                        fontWeight: 700, color: C.body, fontSize: 11,
                        textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {announcements.map((ann, i) => (
                    <tr key={ann.notice_id} style={{ borderBottom: `1px solid ${C.grid}` }}>
                      <td style={{ padding: "10px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                        <span style={{
                          background: ann.announcement_type_name === "Financial Results" ? "#fff4e8" :
                                      ann.announcement_type_name === "Board Meetings"   ? "#e7f7f0" : "#eef2f9",
                          color:      ann.announcement_type_name === "Financial Results" ? C.orange :
                                      ann.announcement_type_name === "Board Meetings"   ? C.green : C.navy,
                          padding: "3px 9px", borderRadius: 5, fontSize: 11, fontWeight: 600,
                        }}>{ann.announcement_type_name}</span>
                      </td>
                      <td style={{ padding: "10px 16px", textAlign: "center", color: C.muted, whiteSpace: "nowrap", fontSize: 12 }}>
                        {ann.announcement_date}
                      </td>
                      <td style={{ padding: "10px 16px", color: C.body }}>
                        {ann.description}
                        {ann.is_revised && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: C.orange, fontWeight: 600 }}>(Revised)</span>
                        )}
                      </td>
                      <td style={{ padding: "10px 16px", textAlign: "center" }}>
                        {ann.pdf_url
                          ? <a href={ann.pdf_url} target="_blank" rel="noopener noreferrer"
                               style={{ color: C.red, fontWeight: 700, fontSize: 12, textDecoration: "none" }}>PDF</a>
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── FINANCIALS ── */}
        {financials && (
          <>
            {/* Earnings */}
            {financials.earnings && (
              <div className="td-grid-2" style={{ marginBottom: "1.25rem" }} ref={secRefs.earnings}>
                <div className="card" style={{ marginBottom: 0 }}>
                  <div className="card-header"><h2 className="card-title">Earnings</h2></div>
                  <div className="card-content">
                    <div className="data-rows">
                      <InfoRow label="Latest EPS"      value={financials.earnings.latest_eps != null ? `Rs. ${fmtNum(financials.earnings.latest_eps)}` : null} />
                      <InfoRow label="Last Annual EPS" value={financials.earnings.last_annual_eps != null ? `Rs. ${fmtNum(financials.earnings.last_annual_eps)}` : null} />
                      <InfoRow label="Trailing EPS"    value={financials.earnings.trailing_eps != null ? `Rs. ${fmtNum(financials.earnings.trailing_eps)}` : null} />
                      <InfoRow label="P/E Ratio"       value={financials.earnings.pe_ratio != null ? `${fmtNum(financials.earnings.pe_ratio)} x` : null} />
                      <InfoRow label="EPS Growth"      value={financials.earnings.eps_growth != null ? `${fmtNum(financials.earnings.eps_growth)}%` : null} />
                    </div>
                  </div>
                </div>

                <div className="card" style={{ marginBottom: 0 }}>
                  <div className="card-header"><h2 className="card-title">Annual EPS</h2></div>
                  <div className="card-content" style={{ paddingTop: 6 }}>
                    {financials.earnings.annual_chart?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={financials.earnings.annual_chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="35%">
                          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                          <XAxis dataKey="year" tick={axisTick} tickLine={false} axisLine={{ stroke: C.line }} />
                          <YAxis tick={axisTick} tickLine={false} axisLine={false} width={38} />
                          <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                          <Bar dataKey="eps" name="EPS" fill={C.green} radius={[2, 2, 0, 0]} maxBarSize={18} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <div style={{ textAlign: "center", color: C.faint, padding: "50px 0", fontSize: 13 }}>No data</div>}
                  </div>
                </div>
              </div>
            )}

            {/* Quarterly EPS chart */}
            {financials.earnings?.quarterly_chart?.length > 0 && (
              <div className="card summary-card" ref={secRefs.eps}>
                <div className="card-header"><h2 className="card-title">Quarterly EPS</h2></div>
                <div className="card-content" style={{ paddingTop: 6 }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={financials.earnings.quarterly_chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="28%">
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                      <XAxis dataKey="year" tick={axisTick} tickLine={false} axisLine={{ stroke: C.line }} />
                      <YAxis tick={axisTick} tickLine={false} axisLine={false} width={38} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      <Bar dataKey="Q1" name="Q1" fill="#5b8def" radius={[2, 2, 0, 0]} maxBarSize={12} />
                      <Bar dataKey="Q2" name="Q2" fill={C.navy}   radius={[2, 2, 0, 0]} maxBarSize={12} />
                      <Bar dataKey="Q3" name="Q3" fill={C.green}  radius={[2, 2, 0, 0]} maxBarSize={12} />
                      <Bar dataKey="Q4" name="Q4" fill={C.orange} radius={[2, 2, 0, 0]} maxBarSize={12} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Dividends */}
            {financials.dividends && (
              <div className="td-grid-2" style={{ marginBottom: "1.25rem" }} ref={secRefs.dividends}>
                <div className="card" style={{ marginBottom: 0 }}>
                  <div className="card-header"><h2 className="card-title">Dividends</h2></div>
                  <div className="card-content">
                    <div className="data-rows">
                      <InfoRow label="Latest Dividend"      value={financials.dividends.latest_dividend != null ? `Rs. ${fmtNum(financials.dividends.latest_dividend)}` : null} />
                      <InfoRow label="Last Annual Dividend" value={financials.dividends.last_annual_dividend != null ? `Rs. ${fmtNum(financials.dividends.last_annual_dividend)}` : null} />
                    </div>
                    {financials.dividends.history?.length === 0 && (
                      <div style={{ textAlign: "center", color: C.faint, padding: "20px 0", fontSize: 13 }}>No dividend history</div>
                    )}
                  </div>
                </div>

                {financials.dividends.history?.length > 0 && (
                  <div className="card" style={{ marginBottom: 0 }}>
                    <div className="card-header"><h2 className="card-title">Dividend History</h2></div>
                    <div className="card-content" style={{ paddingTop: 6 }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={financials.dividends.history} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="35%">
                          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                          <XAxis dataKey="year" tick={axisTick} tickLine={false} axisLine={{ stroke: C.line }} />
                          <YAxis tick={axisTick} tickLine={false} axisLine={false} width={38} />
                          <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                          <Bar dataKey="dividend" name="Dividend" fill={C.orange} radius={[2, 2, 0, 0]} maxBarSize={18} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Profit history */}
            {financials.profit?.history?.length > 0 && (
              <div className="card summary-card" ref={secRefs.financials}>
                <div className="card-header"><h2 className="card-title">Profit History</h2></div>
                <div className="card-content" style={{ paddingTop: 6 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={financials.profit.history} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                      <XAxis dataKey="year" tick={axisTick} tickLine={false} axisLine={{ stroke: C.line }} />
                      <YAxis tick={axisTick} tickLine={false} axisLine={false} width={54}
                             tickFormatter={v => (v / 1000).toFixed(0) + "B"} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} formatter={(v) => [(v / 1000).toFixed(2) + " B", undefined]} />
                      <Bar dataKey="profit_before_tax" name="Profit Before Tax" fill={C.navy}  radius={[2, 2, 0, 0]} maxBarSize={16} />
                      <Bar dataKey="profit_after_tax"  name="Profit After Tax"  fill={C.green} radius={[2, 2, 0, 0]} maxBarSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

          </>
        )}

        {/* ── OHLCV TABLE ── */}
        <div className="card summary-card" ref={secRefs.ohlcv}>
          <div className="card-header" style={{ justifyContent: "space-between" }}>
            <h2 className="card-title">Recent OHLCV Data</h2>
            <div className="td-range-btns">
              {RANGES.slice(0, 3).map(r => (
                <RangeBtn key={r.label} label={r.label} active={activeRange.label === r.label} onClick={() => setActiveRange(r)} />
              ))}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 520 }}>
              <thead>
                <tr style={{ background: "#f8f9fb", borderBottom: `2px solid ${C.line}` }}>
                  {["Date", "Open", "High", "Low", "Close", "Volume", "Prev Close"].map(h => (
                    <th key={h} style={{
                      padding: "10px 16px", textAlign: h === "Date" ? "left" : "right",
                      fontWeight: 700, color: C.body, fontSize: 11,
                      textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...chartData].reverse().slice(0, 15).map((row, i) => {
                  const up = row.ldcp != null ? row.close >= row.ldcp : true;
                  return (
                    <tr key={row.date + i} style={{ borderBottom: `1px solid ${C.grid}` }}>
                      <td style={{ padding: "9px 16px", color: C.body, fontWeight: 500, whiteSpace: "nowrap" }}>{row.date}</td>
                      {[row.open, row.high, row.low].map((v, j) => (
                        <td key={j} style={{ padding: "9px 16px", textAlign: "right", color: C.muted }}>{v?.toFixed(2) ?? "—"}</td>
                      ))}
                      <td style={{ padding: "9px 16px", textAlign: "right", fontWeight: 700, color: up ? C.green : C.red }}>
                        {row.close?.toFixed(2) ?? "—"}
                      </td>
                      <td style={{ padding: "9px 16px", textAlign: "right", color: C.muted }}>
                        {row.volume?.toLocaleString() ?? "—"}
                      </td>
                      <td style={{ padding: "9px 16px", textAlign: "right", color: C.faint }}>
                        {row.ldcp?.toFixed(2) ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PSXDashboard;
